/**
 * Per-state tax profiles (spec §9). Tiered honesty:
 *   'modeled' | 'flat_approximation' | 'none' — fidelity and lastVerified are
 * surfaced in the UI so an approximation is never mistaken for exact math.
 *
 * Data researched 2026-08-03 against Tax Foundation's 2026 tables, state
 * revenue departments (NJ Div. of Taxation, MI Treasury RAB 2026-1, WA DOR),
 * and Kiplinger's 2026 state-SS coverage. Notable 2026 facts encoded here:
 *   - OH went flat 1/1/2026: 2.75% on income over $26,050.
 *   - NC 3.99%, KY 3.50%, IN 2.95%, MS 4.00% — all cut 1/1/2026.
 *   - WV finished its SS phase-out: 100% exempt from 2026.
 *   - MI's retirement-subtraction phase-in completed: $67,610/$135,220.
 *   - CT IRA deduction reaches 100% in 2026 (income-limited).
 *   - NJ's pension exclusion dies entirely above $150k income — modeled here
 *     conservatively with the full-exclusion tier's $100k limit as the cliff.
 * Eight states still tax SS to some degree: CO, CT, MN, MT, NM, RI, UT, VT —
 * most exempt below income thresholds; where a threshold covers the common
 * case we mark SS untaxed and say so in the note.
 */
import type { FilingStatus } from '../../model/types';
import type { StateTaxProfile } from '../tax/state';
import type { Bracket } from './federal';

export const STATE_DATA_AS_OF = '2026-08-03';

const V = STATE_DATA_AS_OF;

function sameForAll(brackets: Bracket[]): Record<FilingStatus, Bracket[]> {
  return { single: brackets, mfj: brackets, hoh: brackets, qw: brackets };
}
function bySingleMfj(single: Bracket[], mfj: Bracket[]): Record<FilingStatus, Bracket[]> {
  return { single, mfj, hoh: single, qw: mfj };
}
function flat(rate: number): Record<FilingStatus, Bracket[]> {
  return sameForAll([{ rate, floor: 0 }]);
}

const PROFILES: Record<string, StateTaxProfile> = {};

// ── No-income-tax states (exact) ────────────────────────────────────────────
const NO_TAX: [string, string, string][] = [
  ['AK', 'Alaska', 'No state income tax.'],
  ['FL', 'Florida', 'No state income tax.'],
  ['NV', 'Nevada', 'No state income tax.'],
  ['NH', 'New Hampshire', 'No income tax; the interest & dividends tax was repealed effective 2025.'],
  ['SD', 'South Dakota', 'No state income tax.'],
  ['TN', 'Tennessee', 'No state income tax.'],
  ['TX', 'Texas', 'No state income tax.'],
  ['WY', 'Wyoming', 'No state income tax.'],
  ['WA', 'Washington', 'No income tax. WA has a 7-9.9% capital-gains excise above a ~$278k exemption — not modeled; retirement-account distributions are exempt from it.'],
];
for (const [code, name, notes] of NO_TAX) {
  PROFILES[code] = {
    code, name, fidelity: 'none',
    socialSecurityTaxed: false, capitalGainsTreatment: 'excluded',
    notes, lastVerified: V,
  };
}

// ── Flat-tax states (modeled) ───────────────────────────────────────────────
interface FlatSpec {
  code: string; name: string; rate: number; notes: string;
  ssTaxed?: boolean; ssNote?: string;
  exclusion?: StateTaxProfile['retirementIncomeExclusion'];
  extraBrackets?: Record<FilingStatus, Bracket[]>;
}
const FLAT_STATES: FlatSpec[] = [
  { code: 'AZ', name: 'Arizona', rate: 0.025, notes: 'Flat 2.5%.' },
  { code: 'CO', name: 'Colorado', rate: 0.044, notes: 'Flat 4.4% (TABOR can trigger temporary cuts). SS fully deductible at 65+; ages 55-64 deduct fully under AGI limits — modeled as untaxed.' },
  { code: 'GA', name: 'Georgia', rate: 0.0519, notes: 'Flat 5.19%, scheduled to fall toward 4.99%. Retirement exclusion $65k/person at 65+ ($35k at 62-64 — 65+ figure modeled).',
    exclusion: { maxAmount: { single: 65_000, mfj: 130_000, hoh: 65_000, qw: 130_000 }, minAge: 65 } },
  { code: 'ID', name: 'Idaho', rate: 0.053, notes: 'Flat 5.3% (2025 cut).' },
  { code: 'IL', name: 'Illinois', rate: 0.0495, notes: 'Flat 4.95%. All qualified retirement income (SS, pensions, 401(k), IRA) is EXEMPT — a major retiree advantage.',
    exclusion: { maxAmount: { single: 9_999_999_999, mfj: 9_999_999_999, hoh: 9_999_999_999, qw: 9_999_999_999 }, minAge: 0 } },
  { code: 'IN', name: 'Indiana', rate: 0.0295, notes: 'Flat 2.95% (2026 cut). Counties add a local income tax (~1-2%) — not modeled.' },
  { code: 'IA', name: 'Iowa', rate: 0.038, notes: 'Flat 3.8%. Retirement income is exempt for 55+ — modeled.',
    exclusion: { maxAmount: { single: 9_999_999_999, mfj: 9_999_999_999, hoh: 9_999_999_999, qw: 9_999_999_999 }, minAge: 55 } },
  { code: 'KY', name: 'Kentucky', rate: 0.035, notes: 'Flat 3.5% (2026 cut). $31,110/person pension exclusion.',
    exclusion: { maxAmount: { single: 31_110, mfj: 62_220, hoh: 31_110, qw: 62_220 }, minAge: 0 } },
  { code: 'LA', name: 'Louisiana', rate: 0.03, notes: 'Flat 3.0%.' },
  { code: 'MI', name: 'Michigan', rate: 0.0425, notes: 'Flat 4.25%. The 2023 law\'s phase-in is complete: retirement subtraction up to $67,610 single / $135,220 joint (2026).',
    exclusion: { maxAmount: { single: 67_610, mfj: 135_220, hoh: 67_610, qw: 135_220 }, minAge: 62 } },
  { code: 'MS', name: 'Mississippi', rate: 0.04, notes: 'Flat 4.0% (phasing toward 3% by 2030). Qualified retirement income is exempt.',
    exclusion: { maxAmount: { single: 9_999_999_999, mfj: 9_999_999_999, hoh: 9_999_999_999, qw: 9_999_999_999 }, minAge: 59 } },
  { code: 'NC', name: 'North Carolina', rate: 0.0399, notes: 'Flat 3.99% (2026 cut).' },
  { code: 'PA', name: 'Pennsylvania', rate: 0.0307, notes: 'Flat 3.07% — but SS, pensions, and 401(k)/IRA distributions after retirement age are ALL exempt. One of the friendliest states for retirees.',
    exclusion: { maxAmount: { single: 9_999_999_999, mfj: 9_999_999_999, hoh: 9_999_999_999, qw: 9_999_999_999 }, minAge: 59 } },
  { code: 'UT', name: 'Utah', rate: 0.045, notes: 'Flat 4.5%. UT taxes SS but a credit offsets it below ~$54k single / $90k joint income — modeled as taxed (conservative for higher incomes).',
    ssTaxed: true },
];
for (const s of FLAT_STATES) {
  PROFILES[s.code] = {
    code: s.code, name: s.name, fidelity: 'modeled',
    brackets: s.extraBrackets ?? flat(s.rate),
    standardDeduction: undefined,
    socialSecurityTaxed: s.ssTaxed ?? false,
    retirementIncomeExclusion: s.exclusion,
    capitalGainsTreatment: 'as_ordinary',
    notes: s.notes, lastVerified: V,
  };
}

// Ohio: flat 2.75% but only on income over $26,050 (2026, newly flat).
PROFILES['OH'] = {
  code: 'OH', name: 'Ohio', fidelity: 'modeled',
  brackets: sameForAll([{ rate: 0, floor: 0 }, { rate: 0.0275, floor: 26_050 }]),
  socialSecurityTaxed: false,
  capitalGainsTreatment: 'as_ordinary',
  notes: 'Flat 2.75% on income over $26,050 (2026 — newly flat). SS exempt.',
  lastVerified: V,
};

// Massachusetts: 5% + 4% millionaire surtax.
PROFILES['MA'] = {
  code: 'MA', name: 'Massachusetts', fidelity: 'modeled',
  brackets: sameForAll([{ rate: 0.05, floor: 0 }, { rate: 0.09, floor: 1_107_750 }]),
  socialSecurityTaxed: false,
  capitalGainsTreatment: 'as_ordinary',
  notes: '5% flat + 4% surtax over $1,107,750 (2026). SS exempt.',
  lastVerified: V,
};

// ── Big progressive states (modeled) ────────────────────────────────────────
PROFILES['CA'] = {
  code: 'CA', name: 'California', fidelity: 'modeled',
  brackets: bySingleMfj(
    [
      { rate: 0.01, floor: 0 }, { rate: 0.02, floor: 11_079 }, { rate: 0.04, floor: 26_264 },
      { rate: 0.06, floor: 41_452 }, { rate: 0.08, floor: 57_542 }, { rate: 0.093, floor: 72_724 },
      { rate: 0.103, floor: 371_479 }, { rate: 0.113, floor: 445_771 }, { rate: 0.123, floor: 742_953 },
      { rate: 0.133, floor: 1_000_000 }, // incl. 1% mental-health surtax
    ],
    [
      { rate: 0.01, floor: 0 }, { rate: 0.02, floor: 22_158 }, { rate: 0.04, floor: 52_528 },
      { rate: 0.06, floor: 82_904 }, { rate: 0.08, floor: 115_084 }, { rate: 0.093, floor: 145_448 },
      { rate: 0.103, floor: 742_958 }, { rate: 0.113, floor: 891_542 }, { rate: 0.123, floor: 1_485_906 },
      { rate: 0.133, floor: 1_485_906 + 1 }, // surtax band starts at $1M of TI but MFJ 12.3% starts above it
    ],
  ),
  standardDeduction: { single: 5_540, mfj: 11_080, hoh: 11_080, qw: 11_080 },
  socialSecurityTaxed: false,
  capitalGainsTreatment: 'as_ordinary',
  notes: 'Fully modeled 2026 brackets incl. the 1% surtax over $1M. SS exempt; capital gains taxed as ordinary income. CA finalizes its own 2026 indexing mid-year.',
  lastVerified: V,
};

PROFILES['NY'] = {
  code: 'NY', name: 'New York', fidelity: 'modeled',
  brackets: bySingleMfj(
    [
      { rate: 0.039, floor: 0 }, { rate: 0.044, floor: 8_500 }, { rate: 0.0515, floor: 11_700 },
      { rate: 0.054, floor: 13_900 }, { rate: 0.059, floor: 80_650 }, { rate: 0.0685, floor: 215_400 },
      { rate: 0.0965, floor: 1_077_550 }, { rate: 0.103, floor: 5_000_000 }, { rate: 0.109, floor: 25_000_000 },
    ],
    [
      { rate: 0.039, floor: 0 }, { rate: 0.044, floor: 17_150 }, { rate: 0.0515, floor: 23_600 },
      { rate: 0.054, floor: 27_900 }, { rate: 0.059, floor: 161_550 }, { rate: 0.0685, floor: 323_200 },
      { rate: 0.0965, floor: 2_155_350 }, { rate: 0.103, floor: 5_000_000 }, { rate: 0.109, floor: 25_000_000 },
    ],
  ),
  standardDeduction: { single: 8_000, mfj: 16_050, hoh: 11_200, qw: 16_050 },
  socialSecurityTaxed: false,
  retirementIncomeExclusion: {
    maxAmount: { single: 20_000, mfj: 40_000, hoh: 20_000, qw: 40_000 },
    minAge: 60, // statute: 59½
  },
  capitalGainsTreatment: 'as_ordinary',
  notes: 'Fully modeled 2026 brackets. SS exempt; government pensions fully exempt (not modeled separately); $20k/person private pension & IRA exclusion at 59½+. NYC local tax not modeled.',
  lastVerified: V,
};

PROFILES['NJ'] = {
  code: 'NJ', name: 'New Jersey', fidelity: 'modeled',
  brackets: bySingleMfj(
    [
      { rate: 0.014, floor: 0 }, { rate: 0.0175, floor: 20_000 }, { rate: 0.035, floor: 35_000 },
      { rate: 0.0553, floor: 40_000 }, { rate: 0.0637, floor: 75_000 }, { rate: 0.0897, floor: 500_000 },
      { rate: 0.1075, floor: 1_000_000 },
    ],
    [
      { rate: 0.014, floor: 0 }, { rate: 0.0175, floor: 20_000 }, { rate: 0.0245, floor: 50_000 },
      { rate: 0.035, floor: 70_000 }, { rate: 0.0553, floor: 80_000 }, { rate: 0.0637, floor: 150_000 },
      { rate: 0.0897, floor: 500_000 }, { rate: 0.1075, floor: 1_000_000 },
    ],
  ),
  // NJ has no standard deduction (only small personal exemptions — not modeled).
  socialSecurityTaxed: false,
  retirementIncomeExclusion: {
    maxAmount: { single: 75_000, mfj: 100_000, hoh: 75_000, qw: 100_000 },
    incomeLimit: 100_000, // full exclusion ≤$100k; partial to $150k; $0 above —
    // modeled conservatively as a cliff at the full-exclusion limit.
    minAge: 62,
  },
  capitalGainsTreatment: 'as_ordinary',
  notes: 'Fully modeled 2026 brackets. SS exempt. Age-62+ pension exclusion ($75k single / $100k joint) DIES ENTIRELY above $150k income — modeled as a cliff at the $100k full-exclusion tier. Watch withdrawals near the line.',
  lastVerified: V,
};

// ── Everything else: labeled flat approximations ────────────────────────────
const APPROX: [string, string, number, string][] = [
  ['AL', 'Alabama', 0.045, 'SS and defined-benefit pensions exempt (not modeled) — effective rate is often lower for retirees.'],
  ['AR', 'Arkansas', 0.039, 'Top rate 3.9%.'],
  ['CT', 'Connecticut', 0.05, 'Progressive 2-6.99%. Pension/annuity and (from 2026) 100% of IRA income deductible under $75k/$100k AGI, phasing out to $100k/$150k — a steep phase-out band, not modeled.'],
  ['DC', 'District of Columbia', 0.07, 'Progressive 4-10.75%.'],
  ['DE', 'Delaware', 0.055, 'Progressive to 6.6%; $12.5k pension exclusion at 60+ (not modeled).'],
  ['HI', 'Hawaii', 0.07, 'Progressive to 11%; employer pensions exempt (not modeled).'],
  ['KS', 'Kansas', 0.053, 'Two rates 5.2/5.58%; SS exempt since 2024.'],
  ['MD', 'Maryland', 0.07, 'State 2-5.75% PLUS county piggyback (~2.3-3.2%) — approximation includes a typical local rate.'],
  ['ME', 'Maine', 0.06, 'Progressive 5.8-7.15%; $45,864 (2026) pension deduction (not modeled).'],
  ['MN', 'Minnesota', 0.065, 'Progressive 5.35-9.85%. SS fully subtracted below ~$86k/$111k, phasing out — approximation ignores SS taxation.'],
  ['MO', 'Missouri', 0.042, 'Top 4.7%; SS exempt; public pension exclusions (not modeled).'],
  ['MT', 'Montana', 0.055, 'Two rates 4.7/5.9%. Taxes SS to the federal extent; $5,500 senior subtraction (approximation ignores both).'],
  ['ND', 'North Dakota', 0.02, 'Very low progressive 1.95/2.5%.'],
  ['NE', 'Nebraska', 0.045, 'Top 4.55% (falling); SS exempt since 2024.'],
  ['NM', 'New Mexico', 0.049, 'Progressive 1.7-5.9%. SS EXEMPT only below $100k single / $150k joint AGI — a hard cliff above (not modeled).'],
  ['OK', 'Oklahoma', 0.045, 'Top 4.75%; $10k pension exclusion (not modeled).'],
  ['OR', 'Oregon', 0.08, 'Progressive 4.75-9.9% — one of the highest; no sales tax though.'],
  ['RI', 'Rhode Island', 0.045, 'Progressive 3.75-5.99%. SS and some pension income exempt below indexed income limits (not modeled).'],
  ['SC', 'South Carolina', 0.045, 'Top 6.2% but large retirement/senior deductions at 65+ ($10k + $15k) make effective rates low (not modeled).'],
  ['VA', 'Virginia', 0.05, 'Mostly 5.75% over $17k. Age-65+ deduction up to $12k phases out over $50k/$75k AGI (not modeled).'],
  ['VT', 'Vermont', 0.06, 'Progressive 3.35-8.75%. SS exempt below $55k/$70k AGI, phasing out over $10k (not modeled).'],
  ['WI', 'Wisconsin', 0.05, 'Progressive 3.5-7.65%.'],
  ['WV', 'West Virginia', 0.045, 'Rates falling; SS 100% exempt from 2026 (phase-out complete).'],
];
for (const [code, name, rate, note] of APPROX) {
  PROFILES[code] = {
    code, name, fidelity: 'flat_approximation',
    approxEffectiveRate: rate,
    socialSecurityTaxed: false,
    capitalGainsTreatment: 'as_ordinary',
    notes: `Flat ${(rate * 100).toFixed(1)}% approximation. ${note} Set a custom rate to refine.`,
    lastVerified: V,
  };
}

export function stateProfileFor(code: string): StateTaxProfile {
  return (
    PROFILES[code] ?? {
      code, name: code, fidelity: 'flat_approximation',
      approxEffectiveRate: 0.045,
      socialSecurityTaxed: false, capitalGainsTreatment: 'as_ordinary',
      notes: 'Unknown state code — generic 4.5% approximation.',
      lastVerified: V,
    }
  );
}

export const STATE_PROFILES = PROFILES;
